/**
 * BO status badge dropdown for confirming the global BO value in the
 * "Mark as completed" modal. The prompt label is org-aware: Sunstate Urology
 * sees "Confirm the estimate value" while other orgs keep the global BO wording.
 *
 * @module commentRPAnewModal/components/ProcessingMethodSection
 */
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import { getBoConfirmationLabel } from "../../../../utils/organizationHelper";
import BadgeDropdown from "../../../badgeDropdown/badgeDropdown";

/** Props for {@link ProcessingMethodSection}. */
interface ProcessingMethodSectionProps {
  /** Currently selected BO badge. */
  selectedBadge: BadgeWithIconProps | null;
  /** Setter for the selected BO badge. */
  setSelectedBadge: (badge: BadgeWithIconProps | null) => void;
  /** Available BO status badge options. */
  boStatusOptions: BadgeWithIconProps[];
  /** Fires when the BO dropdown container is clicked. */
  onDropdownClicked: (defaultValue: string) => void;
  /** Fires when a BO dropdown option is selected. */
  onDropdownSelected: (defaultValue: string, selectedValue: string) => void;
}

/**
 * Renders the BO status badge dropdown for confirming the global BO value.
 * The prompt label resolves via {@link getBoConfirmationLabel} for the current facility.
 *
 * @param props - Form state, badge options, and analytics callbacks
 * @returns The BO confirmation JSX section
 */
const ProcessingMethodSection = ({
  selectedBadge,
  setSelectedBadge,
  boStatusOptions,
  onDropdownClicked,
  onDropdownSelected,
}: ProcessingMethodSectionProps) => {
  return (
    <div
      className="flex flex-row items-center gap-2"
      onClick={() => onDropdownClicked(selectedBadge?.id ?? "")}
    >
      <div className="text-sm font-normal text-primaryGray-1">
        {getBoConfirmationLabel()}{" "}
      </div>
      <BadgeDropdown
        onDefaultBadgeClick={() => {}}
        selectedBadge={selectedBadge}
        badgeList={boStatusOptions
          .sort((a, b) => a.id.localeCompare(b.id))
          .filter((status) => status.id !== "")}
        onClick={(badgeId) => {
          onDropdownSelected(selectedBadge?.id ?? "", badgeId);
          const match = boStatusOptions.find((option) => option.id === badgeId);
          setSelectedBadge(match || null);
        }}
      />
    </div>
  );
};

export default ProcessingMethodSection;
